import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { stylesheet } from 'astroturf/react';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { ButtonsPage } from './pages/ButtonsPage';
import { EmailEntryPage } from './pages/EmailEntryPage';
import { EmailRegistrationPage } from './pages/EmailRegistrationPage';
import { EmailLoginPage } from './pages/EmailLoginPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { FacebookDisabledPage } from './pages/FacebookDisabledPage';
import { LoadingPage } from './pages/LoadingPage';
import { trackClick } from 'js/utils/trackClick';
import {
  ConfigurableText,
  CtaLayout,
  HeaderVariant,
  ModalPage,
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';

/**
 * This is the main component of the React login modal.
 * It's meant to eventually be used for all login flows on the website.
 * For instructions on adding it to the header bar login buttons for a page, see
 * js/simple_layout/header/setUpHeader.ts
 */

const defaultText: ConfigurableText = {
  buttonsPage: {
    header: 'Welcome to Academia',
    subheader: 'Sign up to get access to over 50 million papers',
    googleButton: 'Continue with Google',
    appleButton: 'Continue with Apple',
    facebookButton: 'Continue with Facebook',
    emailButton: 'Continue with Email',
    disclaimerVerb: 'Continue',
  },
  emailEntryPage: {
    header: 'Continue with Email',
    subheader: 'Sign up or log in to continue.',
  },
  emailRegistrationPage: {
    header: 'Welcome to Academia',
    subheader: 'Sign up to continue.',
    submit: 'Sign Up',
  },
  emailLoginPage: {
    subheader: 'Log in to continue.',
  },
};

export type Props = React.DialogHTMLAttributes<HTMLDialogElement> & {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean; // if true, the postLoginRedirectUrl will be given priority over the redirectUrl sent from the backend
  clickTrackingContext?: PartialClickTrackingOptions; // anything that should be included with click tracking calls
  configuredText?: PartialConfigurableText; // change the wording on the modal
  registrationParams: RegistrationParams; // send additional params to the back end
  contentComponent?: React.FC<{ children: React.ReactNode }>; // component that will wrap all the children of ModalComponent
  onClose?: () => void;
  initialPage?: ModalPage;
  // Structural sockets for autotest variants (default to today's behavior).
  headerVariant?: HeaderVariant;
  ctaLayout?: CtaLayout;
  // Work context, consumed only by the yourPaperCard header variant.
  workTitle?: string;
  authorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
};

const ModalContentStyles = stylesheet`
:global(.ds2-5-modal__content).nogap {
  gap: 0px; /* remove the 24px gap above the empty <form> */
}
`;

export const LoginModal: React.FC<Props> = (props) => {
  const {
    postLoginRedirectUrl,
    forcePostLoginRedirectUrl,
    clickTrackingContext,
    onClose,
    open,
    configuredText,
    registrationParams,
    contentComponent,
    initialPage,
    // Peel these off so they aren't spread onto <Modal>/<dialog> as DOM attrs.
    headerVariant,
    ctaLayout,
    workTitle,
    authorName,
    workThumbnailUrl,
    workYear,
    workViewCount,
    ...otherProps
  } = props;

  const Content = contentComponent || ModalContent;

  const modalRef = useRef<HTMLDialogElement>(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<ModalPage>(
    initialPage ?? 'buttons',
  );

  useEffect(() => {
    if (open) {
      setCurrentPage(initialPage ?? 'buttons');
    }
    // Reset page only when the modal opens, not while it's already open and initialPage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userFirstName, setUserFirstName] = useState<string>('');

  const modalText = useMemo(() => {
    return {
      buttonsPage: {
        ...defaultText.buttonsPage,
        ...configuredText?.buttonsPage,
      },
      emailEntryPage: {
        ...defaultText.emailEntryPage,
        ...configuredText?.emailEntryPage,
      },
      emailRegistrationPage: {
        ...defaultText.emailRegistrationPage,
        ...configuredText?.emailRegistrationPage,
      },
      emailLoginPage: {
        ...defaultText.emailLoginPage,
        ...configuredText?.emailLoginPage,
      },
    };
  }, [configuredText]);

  useEffect(() => {
    modalRef.current?.addEventListener('close', () => {
      trackClick({
        source: 'login_modal_close',
        ...clickTrackingContext,
      });
    });
  }, [clickTrackingContext, modalRef]);

  const onAttempt = useCallback(() => {
    setLoading(true);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    modalRef.current?.close();
    onClose && onClose();
  }, [onClose]);

  const onSuccess = useCallback(
    (redirect?: string) => {
      if (forcePostLoginRedirectUrl && postLoginRedirectUrl) {
        window.location.href = postLoginRedirectUrl;
      } else if (redirect) {
        window.location.href = redirect;
      } else if (postLoginRedirectUrl) {
        window.location.href = postLoginRedirectUrl;
      } else {
        window.location.reload();
      }
    },
    [forcePostLoginRedirectUrl, postLoginRedirectUrl],
  );

  const onError = useCallback(
    ({
      errorMessage,
      errorCodes = undefined,
    }: {
      errorMessage?: string;
      errorCodes?: string[];
    } = {}) => {
      setLoading(false);
      if (errorMessage) {
        setErrorMessage(errorMessage);
      } else {
        let errorMessage = [
          "Sorry, something went wrong! We're looking into it now.",
          'Please choose a different login method or try again in a few minutes.',
        ].join('\n');
        if (errorCodes) {
          errorMessage += `\n\nError code(s): ${errorCodes.join(', ')}`;
        }
        setErrorMessage(errorMessage);
      }
    },
    [],
  );

  const onEmailButtonClick = useCallback(() => {
    setErrorMessage(null);
    trackClick({
      source: 'login_modal_email_button',
      ...clickTrackingContext,
    });

    setCurrentPage('email-entry');
  }, [clickTrackingContext]);

  return (
    <Modal
      ref={modalRef}
      open={open ?? false}
      onClose={handleClose}
      {...otherProps}
    >
      <Content className={ModalContentStyles.nogap}>
        <ButtonsPage
          onAttempt={onAttempt}
          onSuccess={onSuccess}
          onError={onError}
          errorMessage={errorMessage ?? undefined}
          loading={loading}
          onEmailButtonClick={onEmailButtonClick}
          show={currentPage === 'buttons' && !loading}
          setLoading={setLoading}
          clickTrackingContext={clickTrackingContext}
          text={modalText.buttonsPage}
          registrationParams={registrationParams}
          setPage={setCurrentPage}
          headerVariant={headerVariant}
          ctaLayout={ctaLayout}
          workTitle={workTitle}
          authorName={authorName}
          workThumbnailUrl={workThumbnailUrl}
          workYear={workYear}
          workViewCount={workViewCount}
        />
        <form>
          <EmailEntryPage
            setUserEmail={setUserEmail}
            setUserFirstName={setUserFirstName}
            setPage={setCurrentPage}
            show={currentPage === 'email-entry' && !loading}
            clickTrackingContext={clickTrackingContext}
            text={modalText.emailEntryPage}
          />
          <EmailRegistrationPage
            userEmail={userEmail}
            onSuccess={onSuccess}
            setPage={setCurrentPage}
            show={currentPage === 'email-registration' && !loading}
            setLoading={setLoading}
            clickTrackingContext={clickTrackingContext}
            text={modalText.emailRegistrationPage}
            registrationParams={registrationParams}
          />
          <EmailLoginPage
            userFirstName={userFirstName}
            userEmail={userEmail}
            onSuccess={onSuccess}
            setPage={setCurrentPage}
            setLoading={setLoading}
            show={currentPage === 'email-login' && !loading}
            clickTrackingContext={clickTrackingContext}
            text={modalText.emailLoginPage}
            registrationParams={registrationParams}
          />
        </form>
        <PasswordResetPage
          userEmail={userEmail}
          show={currentPage === 'password-reset' && !loading}
          setPage={setCurrentPage}
          modalRef={modalRef}
          clickTrackingContext={clickTrackingContext}
        />
        <FacebookDisabledPage
          show={currentPage === 'facebook-disabled' && !loading}
          setPage={setCurrentPage}
          clickTrackingContext={clickTrackingContext}
        />
        <LoadingPage show={loading} />
      </Content>
    </Modal>
  );
};
